import React, { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  Close as CloseIcon,
  AccountTree as ScopeIcon,
  Cloud as AwsIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import { InvestigationScope } from '../../../types';
import { useAccountSelection } from '../hooks/useAccountSelection';

interface InvestigationScopeModalProps {
  isOpen: boolean;
  customerId: string;
  title: string;
  initialScope?: InvestigationScope[];
  onSave: (scope: InvestigationScope[]) => void;
  onCancel: () => void;
}

const InvestigationScopeModal = memo<InvestigationScopeModalProps>(({
  isOpen,
  customerId,
  title,
  initialScope = [],
  onSave,
  onCancel
}) => {
  const {
    accounts,
    selectedAccountIds,
    loading,
    error,
    retry,
    toggleAccount,
    toggleAll,
    isAccountSelected,
    allSelected,
    buildScope,
  } = useAccountSelection(customerId, isOpen, initialScope);

  const handleSave = () => {
    onSave(buildScope());
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { height: '70vh' },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScopeIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
          </Box>
          <IconButton onClick={onCancel} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Select accounts to include in the investigation scope
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading accounts...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4, px: 3 }}>
            <InlineAlert severity="error" sx={{ mb: 2, width: '100%' }}>
              {error}
            </InlineAlert>
            <Button variant="contained" onClick={retry}>
              Retry
            </Button>
          </Box>
        ) : accounts.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6, px: 3 }}>
            <AwsIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No accounts available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Configure accounts for this customer first.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ p: 2, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle2" color="text.secondary">
                  {selectedAccountIds.length} of {accounts.length} selected
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={toggleAll}
                >
                  {allSelected ? 'Deselect All' : 'Select All'}
                </Button>
              </Box>
            </Box>

            <List sx={{ flex: 1, overflow: 'auto', px: 1 }}>
              {accounts.map(account => (
                <React.Fragment key={account.id}>
                  <ListItem
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      '&:hover': {
                        bgcolor: 'action.hover'
                      }
                    }}
                  >
                    <ListItemIcon>
                      <AwsIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={account.account_name}
                      secondary={
                        <Box component="span">
                          {account.aws_account_id} • {account.main_region}
                        </Box>
                      }
                      primaryTypographyProps={{
                        sx: { fontWeight: 500 }
                      }}
                    />
                    <ListItemSecondaryAction>
                      <Checkbox
                        edge="end"
                        checked={isAccountSelected(account.id)}
                        onChange={(e) => toggleAccount(account.id, e.target.checked)}
                        color="primary"
                      />
                    </ListItemSecondaryAction>
                  </ListItem>
                  <Divider variant="inset" component="li" />
                </React.Fragment>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: 'background.default', borderTop: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <Typography variant="body2" color="text.secondary">
            <strong>{selectedAccountIds.length}</strong> account{selectedAccountIds.length !== 1 ? 's' : ''} selected
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onCancel} variant="outlined">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
            >
              Save Scope
            </Button>
          </Box>
        </Box>
      </DialogActions>
    </Dialog>
  );
});

InvestigationScopeModal.displayName = 'InvestigationScopeModal';

export default InvestigationScopeModal;
